import { useState } from "react";
import { ArrowDownLeft, ArrowUpRight, Building2, Check, Copy, User, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { WebhookDirection, WebhookScope } from "@/types";

/** Shared presentation vocabulary for the webhook surfaces. */

export const DIRECTION_META: Record<
  WebhookDirection,
  { label: string; blurb: string; icon: LucideIcon; color: string }
> = {
  inbound: {
    label: "Inbound",
    blurb: "External services POST events to a Hive URL",
    icon: ArrowDownLeft,
    color: "#10b981",
  },
  outbound: {
    label: "Outbound",
    blurb: "Hive POSTs signed events to your endpoint",
    icon: ArrowUpRight,
    color: "#38bdf8",
  },
};

export const SCOPE_META: Record<
  WebhookScope,
  { label: string; blurb: string; icon: LucideIcon }
> = {
  user: { label: "Personal", blurb: "Only you (and admins) see and manage it", icon: User },
  workspace: { label: "Workspace · team", blurb: "Shared — any member of the org can manage it", icon: Users },
  org: { label: "Organization", blurb: "Org-wide; only admins can manage it", icon: Building2 },
};

export const DELIVERY_TONE: Record<string, string> = {
  success: "#22c55e",
  pending: "#f59e0b",
  failed: "#f43f5e",
  rejected: "#f43f5e",
};

/** Sample curl for pushing an event into an inbound endpoint. */
export function curlFor(url: string): string {
  return `curl -X POST '${url}' \\\n  -H 'Content-Type: application/json' \\\n  -d '{"event":"deploy.finished","service":"api","ok":true}'`;
}

export function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        void navigator.clipboard?.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }}
      className="flex shrink-0 items-center gap-1 rounded-md border border-app px-2 py-1 text-[10px] font-medium text-app-1 transition hover:border-app-strong"
    >
      {copied ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
      {copied ? "Copied" : label}
    </button>
  );
}

/** Monospace value row with a copy affordance (URLs, secrets, snippets). */
export function CodeLine({ text, mask }: { text: string; mask?: boolean }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-app bg-surface-2/40 px-2.5 py-2">
      <code className="min-w-0 flex-1 truncate font-mono text-[11px] text-app-1">
        {mask ? text.replace(/./g, "•") : text}
      </code>
      <CopyButton text={text} />
    </div>
  );
}

export function CodeBlock({ text }: { text: string }) {
  return (
    <div className="relative rounded-lg border border-app bg-surface-2/40">
      <pre className="overflow-x-auto p-3 pr-16 font-mono text-[11px] leading-relaxed text-app-1">{text}</pre>
      <div className="absolute right-2 top-2">
        <CopyButton text={text} />
      </div>
    </div>
  );
}
